import React, { useState } from 'react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../auth/AuthContext';
import { updateMe } from '../api/dashboard';
import { errorCode } from '../api/client';
import PageHeader from '../components/PageHeader';
import './Profile.css';

export const MIN_PASSWORD_LENGTH = 8;

function nameErrorMessage(error) {
  const status = error?.response?.status;
  if (status === 404 || status === 405) return "Name changes aren't available yet.";
  if (errorCode(error) === 'network_error') return "Can't reach the dashboard server. Check your connection.";
  return 'Could not save your name. Please try again.';
}

function passwordErrorMessage(code) {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Your current password is incorrect.';
    case 'auth/weak-password':
      return `Choose a stronger password (at least ${MIN_PASSWORD_LENGTH} characters).`;
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return "Can't reach the sign-in service. Check your connection and try again.";
    default:
      return 'Could not update your password. Please try again.';
  }
}

function Profile() {
  const { nurse, updateNurse } = useAuth();

  const [firstNameInput, setFirstNameInput] = useState(nurse.firstName || '');
  const [lastNameInput, setLastNameInput] = useState(nurse.lastName || '');
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [nameSaved, setNameSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);

  const handleSaveName = async () => {
    const firstName = firstNameInput.trim();
    const lastName = lastNameInput.trim();
    if (!firstName || !lastName) {
      setNameError('First and last name are required.');
      return;
    }
    setSavingName(true);
    setNameError('');
    setNameSaved(false);
    try {
      const saved = await updateMe({ firstName, lastName });
      updateNurse({ firstName: saved.firstName ?? firstName, lastName: saved.lastName ?? lastName });
      setNameSaved(true);
    } catch (err) {
      if (errorCode(err) !== 'unauthenticated') setNameError(nameErrorMessage(err));
    } finally {
      setSavingName(false);
    }
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setSavingPassword(true);
    setPasswordError('');
    setPasswordSaved(false);
    try {
      // Firebase requires a recent sign-in to change the password, so confirm
      // the current one first. This also proves the person at the keyboard is the nurse.
      const user = auth.currentUser;
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, currentPassword));
      await updatePassword(user, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
    } catch (err) {
      setPasswordError(passwordErrorMessage(err.code));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <>
      <PageHeader title="My Profile" />

      <div className="component-container">
        <div className="profile-content">

          <div className="profile-card">
            <h2 className="profile-card-title">Name</h2>
            <div className="profile-name-fields">
              <input
                className="profile-input"
                value={firstNameInput}
                onChange={(e) => { setFirstNameInput(e.target.value); setNameSaved(false); }}
                placeholder="First name"
                aria-label="First name"
                disabled={savingName}
              />
              <input
                className="profile-input"
                value={lastNameInput}
                onChange={(e) => { setLastNameInput(e.target.value); setNameSaved(false); }}
                placeholder="Last name"
                aria-label="Last name"
                disabled={savingName}
              />
              <button type="button" className="title-button" onClick={handleSaveName} disabled={savingName}>
                {savingName ? 'Saving...' : 'Save'}
              </button>
            </div>
            {nameError && <span className="profile-error" role="alert">{nameError}</span>}
            {nameSaved && !nameError && <span className="profile-saved-note">Saved.</span>}
          </div>

          <div className="profile-card">
            <h2 className="profile-card-title">Email</h2>
            <input className="profile-input profile-input-readonly" value={nurse.email} aria-label="Email" disabled readOnly />
            <span className="profile-hint">Your email can't be changed once your account is created.</span>
          </div>

          <div className="profile-card profile-card-inline">
            <h2 className="profile-card-title">Account Type</h2>
            <span className="profile-pill">{nurse.role === 'admin' ? 'Admin' : 'Charge Nurse'}</span>
          </div>

          {nurse.unit && (
            <div className="profile-card profile-card-inline">
              <h2 className="profile-card-title">Unit</h2>
              <span className="profile-pill">{nurse.unit.name}</span>
            </div>
          )}

          <div className="profile-card">
            <h2 className="profile-card-title">Password</h2>
            <div className="profile-password-fields">
              <input
                type="password"
                className="profile-input"
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPasswordSaved(false); }}
                placeholder="Current password"
                aria-label="Current password"
                autoComplete="current-password"
                disabled={savingPassword}
              />
              <input
                type="password"
                className="profile-input"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordSaved(false); }}
                placeholder="New password"
                aria-label="New password"
                autoComplete="new-password"
                disabled={savingPassword}
              />
              <input
                type="password"
                className="profile-input"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordSaved(false); }}
                placeholder="Confirm new password"
                aria-label="Confirm new password"
                autoComplete="new-password"
                disabled={savingPassword}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSavePassword(); }}
              />
              <button
                type="button"
                className="title-button profile-password-submit"
                onClick={handleSavePassword}
                disabled={savingPassword}
              >
                {savingPassword ? 'Updating...' : 'Update Password'}
              </button>
            </div>
            {passwordError && <span className="profile-error" role="alert">{passwordError}</span>}
            {passwordSaved && !passwordError && <span className="profile-saved-note">Password updated.</span>}
          </div>

        </div>
      </div>
    </>
  );
}

export default Profile;
